import { useEffect, useState } from "react";
import ToastMsg from "../../components/common/ToastMsg";
import { CUSTOM_CONFIGURATION } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { Save, Calculator, FileText, MapPin, Cog, Wallet, Percent } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";

/* ----------------------------- theme primitives ---------------------------- */

const SettingCard = ({ icon: Icon, title, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-100 dark:border-gray-700">
            <span className="w-9 h-9 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                <Icon className="h-4.5 w-4.5" />
            </span>
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        </div>
        {children}
    </div>
);

const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800",
            checked ? "bg-sidebar" : "bg-gray-300 dark:bg-gray-600",
            disabled && "opacity-50 cursor-not-allowed"
        )}
    >
        <span
            className={cn(
                "inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform",
                checked ? "translate-x-[22px]" : "translate-x-0.5"
            )}
        />
    </button>
);

const ToggleRow = ({ label, description, checked, onChange }) => (
    <div className="flex items-center justify-between gap-4 py-2.5">
        <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-200">{label}</label>
            {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        </div>
        <Toggle checked={checked} onChange={onChange} />
    </div>
);

const NumberField = ({ label, hint, prefix, value, onChange, min }) => (
    <div>
        <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5">{label}</label>
        <div className="relative">
            {prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                    {prefix}
                </span>
            )}
            <input
                type="number"
                min={min}
                value={value}
                onChange={onChange}
                className={cn(
                    "w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900",
                    "px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100",
                    "focus:outline-none focus:ring-2 focus:ring-sidebar-primary focus:border-sidebar-primary transition",
                    prefix && "pl-7"
                )}
            />
        </div>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
);

/* --------------------------------- screen ---------------------------------- */

const SettingsConfiguration = () => {
    const { fetch, update } = useApiMethods();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [configData, setConfigData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        pf_limit: 15000,
        cap_amount: 1800,
        zero_payroll: true,
        modify_issued_documents: false,
        biometric_punch_only_inout: false,
        grace_times: 0,
        deduct_for_contract_emp: true,
        is_deduction: true,
        loc_track_ctl: false,
        salary_deduction: "gross",
        lop_deduction: "net_salary"
    });

    const fetchCustomConfig = async () => {
        setLoading(true);
        try {
            const response = await fetch(CUSTOM_CONFIGURATION);
            if (response.status === 'success' && response.data && response.data[0]) {
                const data = response.data[0];
                setConfigData(data);
                setFormData({
                    pf_limit: data.pf_limit ?? 15000,
                    cap_amount: data.cap_amount ?? 1800,
                    zero_payroll: data.zero_payroll !== undefined ? data.zero_payroll : true,
                    modify_issued_documents: data.modify_issued_documents || false,
                    biometric_punch_only_inout: data.biometric_punch_only_inout || false,
                    grace_times: data.grace_times || 0,
                    deduct_for_contract_emp: data.deduct_for_contract_emp !== undefined ? data.deduct_for_contract_emp : true,
                    is_deduction: data.is_deduction !== undefined ? data.is_deduction : true,
                    salary_deduction: data.salary_deduction || "gross",
                    lop_deduction: data.lop_deduction || "net_salary",
                    loc_track_ctl: data.loc_track_ctl
                });
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...formData,
                id: configData?.id || 1
            };

            const response = await update(CUSTOM_CONFIGURATION, payload);

            if (response.status === 'success') {
                ToastMsg('success', 'Configuration saved successfully');
                fetchCustomConfig(); // Refresh data
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    useEffect(() => {
        fetchCustomConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="space-y-2">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-5 space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-xl" />
                                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40" />
                            </div>
                            {[...Array(3)].map((_, j) => (
                                <div key={j} className="space-y-2">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                    <Cog className="h-5 w-5" />
                </div>
                <div>
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Custom Configuration</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        Manage company-wide payroll and system configuration settings.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column */}
                <div className="space-y-6">
                    <SettingCard icon={Calculator} title="Provident Fund Settings">
                        <div className="space-y-4">
                            <NumberField
                                label="Basic Salary Limit"
                                prefix="₹"
                                value={formData.pf_limit}
                                onChange={(e) => handleInputChange('pf_limit', parseInt(e.target.value) || 0)}
                                hint="Minimum basic salary for PF eligibility"
                            />
                            <NumberField
                                label="Cap Amount"
                                prefix="₹"
                                value={formData.cap_amount}
                                onChange={(e) => handleInputChange('cap_amount', parseInt(e.target.value) || 0)}
                                hint="Maximum PF contribution amount"
                            />
                            <ToggleRow
                                label="Tax Deduction (PF, ESIC, other…)"
                                description="Enable tax deductions for employees"
                                checked={formData.is_deduction}
                                onChange={(v) => handleInputChange('is_deduction', v)}
                            />
                        </div>
                    </SettingCard>

                    <SettingCard icon={Percent} title="LOP Deduction">
                        <div className="space-y-1">
                            <ToggleRow
                                label="Gross Salary"
                                description="Deduct LOP from gross salary"
                                checked={formData.lop_deduction === 'gross_salary'}
                                onChange={(v) => handleInputChange('lop_deduction', v ? 'gross_salary' : 'net_salary')}
                            />
                            <ToggleRow
                                label="Net Salary"
                                description="Deduct LOP from net salary"
                                checked={formData.lop_deduction === 'net_salary'}
                                onChange={(v) => handleInputChange('lop_deduction', v ? 'net_salary' : 'gross_salary')}
                            />
                        </div>
                    </SettingCard>

                    <SettingCard icon={MapPin} title="Organization Location Tracker">
                        <ToggleRow
                            label="Location Track"
                            description="Enable salesperson location tracking"
                            checked={formData.loc_track_ctl}
                            onChange={(v) => handleInputChange('loc_track_ctl', v)}
                        />
                    </SettingCard>
                </div>

                {/* Right column */}
                <div className="space-y-6">
                    <SettingCard icon={Wallet} title="Payroll Settings">
                        <div className="space-y-1">
                            <ToggleRow
                                label="Allow To Run Payroll For Zero Attendance"
                                description="Enable payroll processing without attendance data"
                                checked={formData.zero_payroll}
                                onChange={(v) => handleInputChange('zero_payroll', v)}
                            />
                            <ToggleRow
                                label="Deduct taxes for contract employee"
                                description="Apply tax deductions to contract employees"
                                checked={formData.deduct_for_contract_emp}
                                onChange={(v) => handleInputChange('deduct_for_contract_emp', v)}
                            />
                        </div>
                    </SettingCard>

                    <SettingCard icon={FileText} title="Document & System Settings">
                        <div className="space-y-3">
                            <ToggleRow
                                label="Allow User To Delete The Issued Documents"
                                description="Permit document deletion after issuance"
                                checked={formData.modify_issued_documents}
                                onChange={(v) => handleInputChange('modify_issued_documents', v)}
                            />
                            <ToggleRow
                                label="Biometric Punch Only IN/OUT"
                                description="Restrict biometric to check-in/check-out only"
                                checked={formData.biometric_punch_only_inout}
                                onChange={(v) => handleInputChange('biometric_punch_only_inout', v)}
                            />
                            <div className="pt-1">
                                <NumberField
                                    label="Grace Times (Minutes)"
                                    min="0"
                                    value={formData.grace_times}
                                    onChange={(e) => handleInputChange('grace_times', parseInt(e.target.value) || 0)}
                                    hint="Allowance time for late arrivals"
                                />
                            </div>
                        </div>
                    </SettingCard>
                </div>
            </div>

            {/* Save */}
            <div className="flex justify-end pt-2">
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-sidebar hover:bg-sidebar-primary text-white font-medium flex items-center gap-2"
                >
                    <Save className="h-4 w-4" />
                    {saving ? 'Saving...' : 'Save Configuration'}
                </Button>
            </div>
        </div>
    );
};

export default SettingsConfiguration;
